// Salary structure (Phase 3a) — itemized earnings that make up the monthly gross.
// Read-only for employees (their own breakdown); editable for HR per employee.
import { useState, useEffect } from 'react';
import { TextField } from '@mui/material';
import { Plus, Trash2 } from 'lucide-react';
import { Card, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { AG } from './theme/tokens';
import { api } from './api';

const CODES = ['BASIC', 'HRA', 'TRANSPORT', 'MEDICAL', 'UTILITIES', 'OTHER'];
const LABELS = { BASIC: 'Basic salary', HRA: 'House rent', TRANSPORT: 'Transport', MEDICAL: 'Medical', UTILITIES: 'Utilities', OTHER: 'Other' };

const BAND_TONE = { within: 'success', below: 'warning', above: 'error', unknown: 'neutral' };
const BAND_LABEL = { within: 'Within band', below: 'Below band', above: 'Above band', unknown: 'No band' };

export default function SalaryStructure({ employeeId, editable = false, showToast }) {
  const [data, setData] = useState(null);
  const [lines, setLines] = useState([]);
  const [band, setBand] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    (employeeId ? api.empStructure(employeeId) : api.myStructure())
      .then((d) => { setData(d); setLines(d.lines.map((l) => ({ code: l.code, label: l.label, amount: l.amount, taxable: l.taxable }))); })
      .catch(() => setData(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [employeeId]);
  // HR-only: where the employee's salary sits in their grade's pay band.
  useEffect(() => {
    if (editable && employeeId) api.payGradeCheck(employeeId).then(setBand).catch(() => setBand(null));
  }, [editable, employeeId]);

  if (!data) return null;
  const cur = data.currency || 'PKR';
  const money = (n) => `${cur} ${Number(n || 0).toLocaleString('en-IN')}`;

  // Employees with no structure defined: render nothing (keeps the payslip clean).
  if (!editable && data.lines.length === 0) return null;

  const editTotal = lines.reduce((a, l) => a + (Number(l.amount) || 0), 0);
  const variance = data.gross - editTotal;
  const varPill = (v) => (v === 0 ? <Pill tone="success">Balanced</Pill> : v > 0 ? <Pill tone="warning">{money(v)} unallocated</Pill> : <Pill tone="error">{money(-v)} over gross</Pill>);

  const save = async () => {
    if (busy) return; setBusy(true);
    try { const d = await api.saveStructure(employeeId, lines.map((l) => ({ code: l.code, label: l.label, amount: Number(l.amount) || 0, taxable: l.taxable !== false }))); setData(d); showToast?.('Salary structure saved'); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };

  if (!editable) {
    return (
      <Card style={{ padding: 20 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
          <h3 style={{ fontWeight: 600, color: AG.text }}>Salary structure</h3>
          {data.variance !== 0 && varPill(data.variance)}
        </div>
        {data.lines.map((l) => (
          <div key={l.id} className="flex items-center justify-between" style={{ fontSize: 14, padding: '9px 0', borderBottom: `1px solid ${AG.divider}` }}>
            <span style={{ color: AG.n500 }}>{l.label}{!l.taxable && <span style={{ marginLeft: 6 }}><Pill tone="neutral">Non-taxable</Pill></span>}</span>
            <span style={{ color: AG.text, fontWeight: 500 }}>{money(l.amount)}</span>
          </div>
        ))}
        <div className="flex items-center justify-between" style={{ fontSize: 14, padding: '9px 0', borderTop: `2px solid ${AG.n200}`, marginTop: 4 }}>
          <span style={{ color: AG.n500 }}>Gross (monthly)</span>
          <span style={{ color: AG.primary, fontWeight: 700 }}>{money(data.gross)}</span>
        </div>
      </Card>
    );
  }

  // HR editor
  return (
    <Card style={{ padding: 20 }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
        <div>
          <h3 style={{ fontWeight: 600, color: AG.text }}>Salary structure</h3>
          <div style={{ fontSize: 12, color: AG.n500, marginTop: 2 }}>Gross {money(data.gross)} · allocated {money(editTotal)}</div>
          {band && band.grade && (() => {
            const bc = band.grade.currency || cur;
            const bmoney = (n) => `${bc} ${Number(n || 0).toLocaleString('en-IN')}`;
            return (
              <div style={{ marginTop: 6 }}>
                <Pill tone={BAND_TONE[band.status] || 'neutral'}>{band.code} {band.status === 'unknown' ? 'other currency' : (BAND_LABEL[band.status] || band.status)}</Pill>
                <span style={{ fontSize: 11.5, color: AG.n500, marginLeft: 6 }}>band {bmoney(band.grade.minSalary)}–{band.grade.maxSalary > 0 ? bmoney(band.grade.maxSalary) : 'open'}</span>
              </div>
            );
          })()}
        </div>
        {varPill(variance)}
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {lines.map((l, i) => (
          <div key={i} className="flex items-center gap-2">
            <select value={l.code} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, code: e.target.value, label: x.label || LABELS[e.target.value] } : x)))} className="ag-input" style={{ width: 130 }}>
              {CODES.map((c) => <option key={c} value={c}>{LABELS[c]}</option>)}
            </select>
            <TextField size="small" placeholder="Label" value={l.label} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} sx={{ flex: 1 }} />
            <TextField size="small" type="number" placeholder="Amount" value={l.amount} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))} sx={{ width: 130 }} />
            <label style={{ fontSize: 12, color: AG.n500, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={l.taxable !== false} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, taxable: e.target.checked } : x)))} /> Taxable
            </label>
            <button onClick={() => setLines(lines.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4 }}><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between" style={{ marginTop: 14 }}>
        <OutlineBtn onClick={() => setLines([...lines, { code: 'BASIC', label: LABELS.BASIC, amount: '', taxable: true }])}><Plus size={14} style={{ marginRight: 4 }} /> Add line</OutlineBtn>
        <PrimaryBtn onClick={save} disabled={busy}>Save structure</PrimaryBtn>
      </div>
    </Card>
  );
}
